import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { changeLoaderStatus } from 'redux/slice/loaderSlice';
import QRCodeStyling from 'qr-code-styling';
import { NotificationManager } from 'react-notifications';
import Strings from 'values/Strings';
import CopyParamsButton from './button/CopyParamsButton';
import { renderArtQr, loadArtwork, decodeCanvas, utf8QrData } from 'services/artQr';
import classes from './QrGenerator.module.css';

const safeStyle = (style) => ({ ...style, gradient: { ...style.gradient,
    colorStops: style.gradient.colorStops.length ? style.gradient.colorStops.map(stop => ({ ...stop })) : [{ offset: 1, color: 'rgba(0,0,0,0)' }] } });

function QrGenerator({ data, contentType, qrCodeSize, backgroundColor, dotsOptions, squareColors, dotColors,
    image, imageOptions, renderMode, artwork, customPattern, artStrength, copyParamsInBuffer, pastParamsInBuffer, downloadParamsFile }) {
    const size = Math.max(1, Math.min(2000, Math.round(qrCodeSize === -1 ? 300 : Number(qrCodeSize) || 300)));
    const dispatch = useDispatch();
    const [result, setResult] = useState<{ blob: Blob; url: string; key: string; note: string } | null>(null);
    const [error, setError] = useState('');
    const generation = useRef(0);
    const [paramsOpen, setParamsOpen] = useState(false);
    const zoomDialog = useRef<HTMLDialogElement>(null);
    const standardOptions = { data, width: size, height: size,
        backgroundOptions: safeStyle(backgroundColor), dotsOptions: safeStyle(dotsOptions),
        cornersSquareOptions: safeStyle(squareColors), cornersDotOptions: safeStyle(dotColors), image,
        imageOptions: { ...imageOptions, imageSize: imageOptions.imageSize || 0.01 },
        ...(contentType === 'contact' ? { margin: 16, qrOptions: { errorCorrectionLevel: 'H' as const } } : {}) };
    // A serialized snapshot also tracks the legacy editor's nested mutations.
    const key = JSON.stringify({ standardOptions, renderMode, artwork, customPattern, artStrength, contentType });
    useEffect(() => { dispatch(changeLoaderStatus(false)); }, [dispatch]);
    useEffect(() => {
        const id = ++generation.current;
        let active = true;

        setError('');
        const timer = window.setTimeout(async () => {
            try {
                const snapshot = JSON.parse(key);
                if (!snapshot.standardOptions.data) throw new Error('Укажите имя или фамилию для QR-контакта.');
                let blob: Blob, note = '';
                if (snapshot.renderMode !== 'standard') {
                    if (snapshot.renderMode === 'art' && !snapshot.artwork) throw new Error('Выберите изображение для QR.');
                    const generated = await renderArtQr({ data: snapshot.standardOptions.data, size: snapshot.standardOptions.width,
                        artwork: snapshot.renderMode === 'art' ? snapshot.artwork : undefined,
                        pattern: snapshot.customPattern, strength: snapshot.artStrength });
                    blob = generated.blob;
                    note = generated.strengthened ? 'Контраст точек усилен для считывания.' : 'Считывание PNG проверено.';
                } else {
                    const qr = new QRCodeStyling({ ...snapshot.standardOptions,
                        ...(/[^\x00-\x7F]/.test(snapshot.standardOptions.data) ? {
                            data: utf8QrData(snapshot.standardOptions.data),
                            qrOptions: { ...snapshot.standardOptions.qrOptions, mode: 'Byte' }
                        } : {}) });
                    blob = await qr.getRawData('png') as Blob;
                    if (!blob) throw new Error('Не удалось создать QR.');
                    if (snapshot.contentType === 'contact') {
                        const source = URL.createObjectURL(blob);
                        try {
                            const img = await loadArtwork(source);
                            const canvas = document.createElement('canvas');
                            canvas.width = canvas.height = snapshot.standardOptions.width;
                            const ctx = canvas.getContext('2d');
                            ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0);
                            if (!decodeCanvas(canvas, snapshot.standardOptions.data)) throw new Error('Контакт не считывается. Увеличьте размер QR или выберите контрастный стиль.');
                            // The white canvas is for scan validation only; export the original transparent PNG.
                            note = 'Считывание QR-контакта проверено.';
                        } finally { URL.revokeObjectURL(source); }
                    }
                }
                if (!active || id !== generation.current) return;
                const objectUrl = URL.createObjectURL(blob);
                setResult({ blob, url: objectUrl, key, note });
            } catch (err) {
                if (active) setError(err instanceof Error ? err.message : 'Не удалось создать QR. Сократите данные или измените настройки.');
            }
        }, 180);
        return () => { active = false; clearTimeout(timer); };
    }, [key]);
    useEffect(() => () => { if (result) URL.revokeObjectURL(result.url); }, [result]);
    const ready = result?.key === key && !error;
    const notifyError = () => NotificationManager.error('Не удалось скопировать. Используйте Download для сохранения PNG.', Strings.notificationsErrorTitle, 4000);
    const download = () => {
        if (!ready) return;
        const source = URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = source; link.download = 'QRTrace.png';
        document.body.appendChild(link); link.click(); link.remove();
        window.setTimeout(() => URL.revokeObjectURL(source), 10000);
    };
    const copyImage = async () => {
        if (!ready) return;
        try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': result.blob })]);
            NotificationManager.success(Strings.notificationsCopyQRCode, Strings.notificationsSuccessTitle, 2500);
        } catch { notifyError(); }
    };
    const copyData = async () => {
        try { await navigator.clipboard.writeText(data); NotificationManager.success(Strings.notificationsCopyData, Strings.notificationsSuccessTitle, 2500); }
        catch { notifyError(); }
    };
    return <div className={classes.qrCodeGenerator}>
        <div className={classes.qrCodeContainer} aria-busy={!ready && !error}>
            {result && !error ? <button type="button" disabled={!ready} className={classes.previewButton} onClick={copyData} aria-label="Скопировать содержимое QR">
                <img className={classes.previewImage} src={result.url} alt="Предпросмотр QR-кода" />
            </button> : <div className={classes.previewPlaceholder} role="status">{error || 'Создаём QR…'}</div>}
        </div>
        <h4>{size} × {size} Px</h4>
        {!error && (result?.note || !ready) && <p className={classes.renderNote} role="status">{!ready ? 'Создаём QR…' : result.note}</p>}
        <div className={classes.eventContainer}>
            <button type="button" className={classes.downloadContainer} disabled={!ready} onClick={download}>Download</button>
            <button type="button" className={classes.copyContainer} disabled={!ready} onClick={copyImage}>Copy</button>
        </div>
        <button type="button" className={classes.zoomToggle} disabled={!ready} onClick={() => zoomDialog.current?.showModal()}>Увеличить QR</button>
        <button type="button" className={classes.paramsToggle} aria-expanded={paramsOpen} aria-controls="qr-export-params" onClick={() => setParamsOpen(open => !open)}>Параметры {paramsOpen ? '−' : '+'}</button>
        <div id="qr-export-params" className={`${classes.eventContainer} ${classes.paramsRow}`} data-open={paramsOpen}>
            <button type="button" className={classes.downloadContainer} onClick={downloadParamsFile}><span className={classes.buttonText}>Download Params</span></button>
            <div className={classes.downloadContainer}><CopyParamsButton pastParamsInBuffer={pastParamsInBuffer} copyParamsInBuffer={copyParamsInBuffer} /></div>
        </div>
        <dialog className={classes.zoomDialog} ref={zoomDialog} aria-label="Увеличенный QR-код">
            <form method="dialog"><button type="submit" autoFocus>Закрыть</button></form>
            {result && <img src={result.url} alt="Увеличенный QR-код" />}
        </dialog>
    </div>;
}
export default QrGenerator;
